'use client';

import * as React from 'react';
import { cn } from '@/lib/utils';

export interface ThemedImageProps {
  src: string | undefined | null;
  fallback?: string;
  alt?: string;
  className?: string;
  fill?: boolean;
  priority?: boolean;
  sizes?: string;
  width?: number | string;
  height?: number | string;
  wrapperClassName?: string;
  /** 
   * Оптимизировать через Next.js /_next/image (ресайз на сервере).
   * Включать только для каталога (много маленьких карточек).
   * Попапы, корзина и т.д. грузят напрямую с CDN — там качество важнее.
   */
  optimized?: boolean;
  [key: string]: unknown;
}

function isExternalUrl(url: string | null | undefined): boolean {
  if (!url || typeof url !== 'string') return false;
  return url.startsWith('http');
}

const QUALITY = 95;

function getOptimizedSrc(src: string, width: number = 1080): string {
  return `/_next/image?url=${encodeURIComponent(src)}&w=${width}&q=${QUALITY}`;
}

function buildSrcSet(src: string): string {
  const widths = [640, 828, 1080, 1200, 1920];
  return widths
    .map(w => `${getOptimizedSrc(src, w)} ${w}w`)
    .join(', ');
}

export const ThemedImage = React.memo<ThemedImageProps>(({
  src: imagePath,
  fallback = '/placeholder.png',
  className,
  alt,
  priority,
  fill,
  sizes,
  width,
  height,
  wrapperClassName,
  optimized = false,
  ...props
}) => {
  // Двухуровневый fallback:
  //   0 = пробуем optimized (/_next/image)
  //   1 = optimized упал → пробуем прямой URL с CDN
  //   2 = и прямой упал → ставим placeholder
  // Why: у /_next/image на проде бывают таймауты/ошибки на отдельных файлах
  // (особенно из WebView ВКонтакте на iOS). Без этого fallback клиент увидит
  // placeholder вместо реальной картинки, хотя файл на CDN жив.
  const [errorStage, setErrorStage] = React.useState<0 | 1 | 2>(0);

  const isExternal = isExternalUrl(imagePath);

  const handleError = React.useCallback(() => {
    setErrorStage((s) => (s < 2 ? ((s + 1) as 0 | 1 | 2) : s));
  }, []);

  React.useEffect(() => {
    setErrorStage(0);
  }, [imagePath]);

  // Если src пустой/не-внешний — сразу обычный режим, fallback внутри displaySrc.
  // Если src — внешний (CDN), используем стадии: optimized → direct → placeholder.

  // Оптимизированный режим — для каталога (72 маленькие карточки)
  // Ресайз через /_next/image: 200-370 KB → 15-30 KB
  if (optimized && isExternal && errorStage === 0) {
    const srcSet = buildSrcSet(imagePath as string);
    const optimizedSrc = getOptimizedSrc(imagePath as string, 1080);

    if (fill) {
      return (
        <img
          src={optimizedSrc}
          srcSet={srcSet}
          sizes={sizes || '(max-width: 640px) 50vw, 300px'}
          alt={alt || ''}
          className={cn('absolute inset-0 w-full h-full object-cover', className)}
          onError={handleError}
          // decoding=auto + translateZ(0) форсят compositor layer и normal-priority декодирование.
          // Без этого WebKit/iOS могут отрисовать <img> только после user interaction (hover/tap),
          // хотя файл уже загружен — известный баг при transition-transform на parent.
          decoding="auto"
          style={{ transform: 'translateZ(0)' }}
          fetchPriority={priority ? 'high' : 'auto'}
        />
      );
    }

    return (
      <img
        src={optimizedSrc}
        srcSet={srcSet}
        sizes={sizes}
        alt={alt || ''}
        className={className}
        onError={handleError}
        decoding="auto"
        style={{ transform: 'translateZ(0)' }}
        fetchPriority={priority ? 'high' : 'auto'}
        {...(width && { width })}
        {...(height && { height })}
      />
    );
  }

  // Стадия 1: /_next/image упал → пробуем прямой URL с CDN.
  // Браузер сам ресайзит средствами CSS (object-cover) — пусть тяжелее, зато видно.
  // Стадия 2: и прямой упал → placeholder.
  const displaySrc = errorStage >= 2 ? fallback : (imagePath || fallback);

  // Обычный режим — напрямую с CDN (попапы, корзина, промо)
  // Полное качество, без ресайза
  if (fill) {
    return (
      <img
        src={displaySrc}
        alt={alt || ''}
        className={cn('absolute inset-0 w-full h-full object-cover', className)}
        onError={handleError}
        decoding="auto"
        style={{ transform: 'translateZ(0)' }}
        fetchPriority={priority ? 'high' : 'auto'}
      />
    );
  }

  return (
    <img
      src={displaySrc}
      alt={alt || ''}
      className={className}
      onError={handleError}
      decoding="auto"
      style={{ transform: 'translateZ(0)' }}
      fetchPriority={priority ? 'high' : 'auto'}
      {...(width && { width })}
      {...(height && { height })}
    />
  );
});

ThemedImage.displayName = 'ThemedImage';

export default ThemedImage;
